import { motion } from 'framer-motion';

/**
 * TextReveal — A reusable scroll-triggered text reveal component.
 * Splits text into words and staggers their entrance from below with opacity.
 *
 * Props:
 * - text: string — The text to reveal
 * - as: string — HTML element to render (default: 'h2')
 * - className: string — Additional CSS classes
 * - delay: number — Initial delay before animation starts (default: 0)
 * - once: boolean — Only animate once (default: true)
 */
export default function TextReveal({ 
  text, 
  as: Component = 'h2', 
  className = '', 
  delay = 0,
  once = true,
  animateOnLoad = false
}) {
  const words = text.split(' ');

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: delay,
      },
    },
  };

  const wordVariants = {
    hidden: {
      y: '100%',
      opacity: 0,
    },
    visible: {
      y: '0%',
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const MotionComponent = motion[Component] || motion.div;

  return (
    <MotionComponent
      variants={containerVariants}
      initial="hidden"
      whileInView={animateOnLoad ? undefined : "visible"}
      animate={animateOnLoad ? "visible" : undefined}
      viewport={animateOnLoad ? undefined : { once, margin: '-5% 0px -10% 0px' }}
      className={`flex flex-wrap items-baseline ${className}`}
      style={{ columnGap: '0.25em' }}
      aria-label={text}
    >
      {words.map((word, index) => (
        <span key={index} className="text-reveal-word" aria-hidden="true">
          <motion.span variants={wordVariants} className="inline-block">
            {word}
          </motion.span>
        </span>
      ))}
    </MotionComponent>
  );
}
